import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './Users.css';

// The Python cloud server. POST /auth/newuser creates a user. The two
// endpoints this page also uses — PUT /auth/users/<id>/station and
// GET /auth/cluster-managers — are narrow additions on top of that: they
// only ever touch associated_station, never email/password/roles.
import { API_BASE } from '../config';
import { tr } from '../i18n';

// Role catalog mirrors the backend matrix (Users 1/2.jpeg). `scope` drives the
// station picker: 'one' = exactly one station, 'multi' = one or more, 'global' =
// no stations (associated_station sent as []). `canCreate` is documentation
// only — the backend enforces who may actually create users.
//
// Roles 6-9 are NOT separate backend role ids. The backend stores them as
// id 2 (Manager) and only roles.name tells them apart — roleAccess.js reads
// that name and maps it back to 6/7/8/9 to decide which pages show. So for
// these, `id` is the frontend id (chip key + roleAccess.js), while
// `backendId` / `backendName` are what actually get sent to /auth/newuser.
// When those two are missing, `id` / `name` are sent as-is.
const ROLES = [
  { id: 1, name: 'Superadmin',      icon: 'fa-crown',       scope: 'global', canCreate: true,
    desc: tr('Developer access — full system control across all stations.') },
  { id: 4, name: 'Admin',           icon: 'fa-user-shield', scope: 'global', canCreate: true,
    desc: tr('Global access across all stations. Can create users.') },
  { id: 3, name: 'Cluster Manager', icon: 'fa-layer-group', scope: 'multi',  canCreate: false,
    desc: tr('Bound to multiple explicitly associated stations.') },
  { id: 2, name: 'Manager',         icon: 'fa-user-tie',    scope: 'one',    canCreate: false,
    desc: tr('Bound to a single associated station.') },
  { id: 5, name: 'Auditor',         icon: 'fa-glasses',     scope: 'global', canCreate: false,
    desc: tr('Report reader — global view of Close, Offloading & Periodic Reports.') },
  { id: 6, name: 'Manufacture',            backendId: 2, backendName: 'Manufacture',
    icon: 'fa-industry',             scope: 'one', canCreate: false,
    desc: tr('Manufacture account bound to a single station.') },
  { id: 7, name: 'Buses Manager',          backendId: 2, backendName: 'Buses Manager',
    icon: 'fa-bus',                  scope: 'one', canCreate: false,
    desc: tr('Buses operations manager bound to a single station.') },
  { id: 8, name: 'Buses Accountant',       backendId: 2, backendName: 'Buses Accountant',
    icon: 'fa-file-invoice-dollar',  scope: 'one', canCreate: false,
    desc: tr('Bound to a single station. Sees Buses Reports only.') },
  // backendName keeps the backend's spelling ("manifacture"). If you fix the
  // spelling on the backend, change it here too — roleAccess.js accepts both.
  { id: 9, name: 'Manufacture Accountant', backendId: 2, backendName: 'manifacture accountant',
    icon: 'fa-calculator',           scope: 'one', canCreate: false,
    desc: tr('Bound to a single station. Sees Reports, Close & Offloading Reports and ATG only.') },
];

const roleById = (id) => ROLES.find((r) => r.id === id);

// ── One editable Cluster Manager row ──────────────────────────────────────
// A separate component (not inlined in the list .map) so each row owns its
// own edit/saving state — editing one manager must not disturb the others,
// and a save failure on one row should not roll back an in-progress edit on
// a different row.
const ClusterManagerRow = ({ manager, stations, authToken, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [serials, setSerials] = useState(manager.associated_station);
  const [pickerValue, setPickerValue] = useState('');
  const [saving, setSaving] = useState(false);

  // The list this row was built from can refresh after another row's save;
  // pick up the new authoritative value unless this row has an edit in
  // flight, in which case the operator's in-progress choices win.
  useEffect(() => {
    if (!editing) setSerials(manager.associated_station);
  }, [manager.associated_station, editing]);

  const stationName = (serial) => {
    const s = stations.find((x) => x.serial_number === serial);
    return s ? s.name : serial;
  };

  const available = stations.filter((s) => !serials.includes(s.serial_number));

  const addSerial = (raw) => {
    const s = String(raw || '').trim();
    if (!s || serials.includes(s)) return;
    setSerials((prev) => [...prev, s]);
  };
  const removeSerial = (s) => setSerials((prev) => prev.filter((x) => x !== s));

  const cancel = () => {
    setSerials(manager.associated_station);
    setPickerValue('');
    setEditing(false);
  };

  const save = async () => {
    if (serials.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: tr("At least one station"),
        text: tr("A Cluster Manager must keep at least one station."),
      });
      return;
    }

    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/auth/users/${manager.id}/station`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify({ associated_station: serials }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) {
        throw new Error(j.error || j.message || `Request failed (${r.status})`);
      }
      Swal.fire({
        icon: 'success',
        title: tr("Updated"),
        text: tr("{0}'s stations were updated.", manager.email),
        timer: 1500,
        showConfirmButton: false,
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      Swal.fire({ icon: 'error', title: tr("Could not update"), text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-row">
      <div className="cm-row-head">
        <span className="cm-email">{manager.email}</span>
        {!editing && (
          <button type="button" className="cm-edit-btn" onClick={() => setEditing(true)}>
            <i className="fas fa-pen" /> {tr("Edit stations")}
          </button>
        )}
      </div>

      {!editing ? (
        <div className="users-chips cm-chips-static">
          {manager.associated_station.length === 0 ? (
            <span className="cm-empty-hint">{tr("No stations assigned.")}</span>
          ) : (
            manager.associated_station.map((s) => (
              <span className="users-chip" key={s} title={s}>
                {stationName(s)}
              </span>
            ))
          )}
        </div>
      ) : (
        <div className="cm-edit-area">
          <div className="users-chips">
            {serials.map((s) => (
              <span className="users-chip" key={s} title={s}>
                {stationName(s)}
                <button type="button" onClick={() => removeSerial(s)} aria-label={tr("Remove {0}", s)}>×</button>
              </span>
            ))}
          </div>

          <select
            className="users-input"
            value={pickerValue}
            onChange={(e) => { addSerial(e.target.value); setPickerValue(''); }}
          >
            <option value="">
              {available.length ? tr("Add a station…") : tr("All stations added")}
            </option>
            {available.map((s) => (
              <option key={s.serial_number} value={s.serial_number}>
                {s.name} ({s.serial_number})
              </option>
            ))}
          </select>

          <div className="cm-edit-actions">
            <button type="button" className="cm-save" disabled={saving} onClick={save}>
              {saving
                ? <><i className="fas fa-circle-notch fa-spin" /> {tr("Saving…")}</>
                : <><i className="fas fa-check" /> {tr("Save")}</>}
            </button>
            <button type="button" className="cm-cancel" disabled={saving} onClick={cancel}>
              {tr("Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Users = ({ token, LogoutTimeout }) => {
  const authToken = token?.access_token || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [roleId, setRoleId] = useState(2);
  const [serials, setSerials] = useState([]); // string[]
  const [pickerValue, setPickerValue] = useState('');
  const [stations, setStations] = useState([]);
  const [saving, setSaving] = useState(false);

  const [clusterManagers, setClusterManagers] = useState([]);
  const [loadingCM, setLoadingCM] = useState(true);
  const [cmError, setCmError] = useState('');

  const role = roleById(roleId);
  const needsStations = role && (role.scope === 'one' || role.scope === 'multi');

  // Station list — same /station/all the reports pages use. The backend
  // validates every entry in associated_station against this list (by
  // LicenseeTraSerialNo), so we only let the user pick from real stations.
  const loadStations = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/station/all`, {
        headers: { Authorization: authToken },
      });
      if (!r.ok) throw new Error('station/all ' + r.status);
      const j = await r.json();
      const list = (j.data || j || [])
        .filter((s) => s && s.serial_number)
        .map((s) => ({ name: s.name, serial_number: s.serial_number }));
      setStations(list);
    } catch (e) {
      // Non-fatal: the user can still type/confirm later; just no dropdown.
      console.error('Could not load stations', e);
    }
  }, [authToken]);

  const loadClusterManagers = useCallback(async () => {
    setLoadingCM(true);
    setCmError('');
    try {
      const r = await fetch(`${API_BASE}/auth/cluster-managers`, {
        headers: { Authorization: authToken },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.message || j.error || `Request failed (${r.status})`);
      setClusterManagers(j.data || []);
    } catch (e) {
      setCmError(e.message || tr("Could not load Cluster Managers."));
    } finally {
      setLoadingCM(false);
    }
  }, [authToken]);

  useEffect(() => { loadStations(); }, [loadStations]);
  useEffect(() => { loadClusterManagers(); }, [loadClusterManagers]);

  const stationName = (serial) => {
    const s = stations.find((x) => x.serial_number === serial);
    return s ? s.name : serial;
  };

  const addSerial = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return;
    setSerials((prev) => {
      if (prev.includes(s)) return prev;
      // Manager (scope 'one') keeps exactly one station — replace.
      return role.scope === 'one' ? [s] : [...prev, s];
    });
  };
  const removeSerial = (s) => setSerials((prev) => prev.filter((x) => x !== s));

  const pickRole = (id) => {
    setRoleId(id);
    const r = roleById(id);
    // Global roles carry no stations; collapse a 'one'-role list to a single entry.
    if (r.scope === 'global') setSerials([]);
    else if (r.scope === 'one') setSerials((prev) => prev.slice(0, 1));
  };

  const resetForm = () => {
    setEmail(''); setPassword(''); setShowPass(false);
    setRoleId(2); setSerials([]); setPickerValue('');
  };

  const validate = () => {
    const e = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return tr('Enter a valid email address.');
    if (password.length < 6) return tr('Password must be at least 6 characters.');
    if (needsStations && serials.length === 0)
      return role.scope === 'one'
        ? tr('{0} must be assigned a station.', tr(role.name))
        : tr('{0} must be assigned at least one station.', tr(role.name));
    if (role.scope === 'one' && serials.length > 1)
      return tr('{0} can be bound to only one station.', tr(role.name));
    return null;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (LogoutTimeout) LogoutTimeout();
    const err = validate();
    if (err) { Swal.fire({ icon: 'warning', title: tr("Check the form"), text: err }); return; }

    const body = {
      email: email.trim(),
      password,
      // Roles 6-9 go out as id 2 + their own name (see ROLES above).
      roles: { id: role.backendId ?? role.id, name: role.backendName ?? role.name },
      associated_station: needsStations ? serials : [],
    };

    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/auth/newuser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) {
        throw new Error(j.error || j.message || `Request failed (${r.status})`);
      }
      Swal.fire({
        icon: 'success',
        title: tr("User created"),
        html: `${tr("<b>{0}</b> was created as <b>{1}</b>.", body.email, tr(role.name))}`,
        confirmButtonColor: '#36d1a7',
      });
      resetForm();
      // A newly created Cluster Manager should show up in the list below
      // without a manual refresh.
      if (role.id === 3) loadClusterManagers();
    } catch (e) {
      Swal.fire({ icon: 'error', title: tr("Could not create user"), text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const available = stations.filter((s) => !serials.includes(s.serial_number));

  return (
    <div className="users-page">
      <div className="users-hero">
        <h1><i className="fas fa-user-plus" />{tr("User Management")}</h1>
        <p>
          {tr("Create accounts for the central reporting platform. Each user’s role decides their station scope and what they can access. Only a Cluster Manager’s station assignment can be edited after creation — everything else is create-only.")}
        </p>
      </div>

      <div className="users-grid users-grid-single">
        {/* ---------- Create form ---------- */}
        <form className="users-card" onSubmit={submit}>
          <h2>{tr("Create a user")}</h2>
          <p className="users-sub">{tr("Fill in the details and assign a role.")}</p>

          <div className="users-field">
            <label>{tr("Email address")}</label>
            <input
              className="users-input"
              type="email"
              autoComplete="off"
              placeholder={tr("name@company.com")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="users-field">
            <label>{tr("Password")}</label>
            <div className="users-pass-wrap">
              <input
                className="users-input"
                type={showPass ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={tr("At least 6 characters")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="users-eye"
                onClick={() => setShowPass((v) => !v)} aria-label={tr("Toggle password")}>
                <i className={`fas ${showPass ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
          </div>

          <div className="users-field">
            <label>{tr("Role")}</label>
            <div className="users-roles">
              {ROLES.map((r) => (
                <div
                  key={r.id}
                  className={`role-chip ${roleId === r.id ? 'is-active' : ''}`}
                  onClick={() => pickRole(r.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickRole(r.id); } }}
                >
                  <span className="role-ico"><i className={`fas ${r.icon}`} /></span>
                  <div className="role-name">
                    {tr(r.name)}
                    {r.canCreate && <span className="role-badge-create">{tr("CAN CREATE")}</span>}
                  </div>
                  <div className="role-scope">
                    {r.scope === 'global' ? tr("Global — all stations")
                      : r.scope === 'multi' ? tr("Multiple stations")
                      : tr("Single station")}
                  </div>
                  <i className="fas fa-check-circle role-check" />
                </div>
              ))}
            </div>
          </div>

          {needsStations && (
            <div className="users-field">
              <label>{role.scope === 'one' ? tr("Associated station") : tr("Associated stations")}</label>
              {serials.length > 0 && (
                <div className="users-chips">
                  {serials.map((s) => (
                    <span className="users-chip" key={s} title={s}>
                      {stationName(s)}
                      <button type="button" onClick={() => removeSerial(s)} aria-label={tr("Remove {0}", s)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <select
                className="users-input"
                value={pickerValue}
                onChange={(e) => { addSerial(e.target.value); setPickerValue(''); }}
              >
                <option value="">
                  {available.length
                    ? (role.scope === 'one' ? tr("Choose a station…") : tr("Add a station…"))
                    : tr("All stations added")}
                </option>
                {available.map((s) => (
                  <option key={s.serial_number} value={s.serial_number}>
                    {s.name} ({s.serial_number})
                  </option>
                ))}
              </select>
              <div className="users-hint">
                {tr("Only registered stations are accepted — the server rejects any serial it doesn’t recognise.")}
              </div>
            </div>
          )}

          <button className="users-submit" type="submit" disabled={saving}>
            {saving
              ? <><i className="fas fa-circle-notch fa-spin" /> {tr("Creating…")}</>
              : <><i className="fas fa-user-plus" /> {tr("Create user")}</>}
          </button>
        </form>
      </div>

      {/* ---------- Cluster Managers: view + edit stations ---------- */}
      {/* Outside .users-grid-single deliberately: that wrapper caps width at
          620px for the create form, which is too narrow once a row carries
          several station chips plus an "add station" dropdown. */}
      <div className="users-cm-section">
        <div className="users-card users-cm-card">
          <h2>{tr("Cluster Managers")}</h2>
          <p className="users-sub">
            {tr("The only accounts whose station assignment can change after creation.")}
          </p>

          {loadingCM && (
            <div className="cm-loading">
              <i className="fas fa-circle-notch fa-spin" /> {tr("Loading…")}
            </div>
          )}

          {!loadingCM && cmError && (
            <div className="cm-error">
              <i className="fas fa-triangle-exclamation" /> {cmError}
              <button type="button" className="cm-retry" onClick={loadClusterManagers}>
                {tr("Retry")}
              </button>
            </div>
          )}

          {!loadingCM && !cmError && clusterManagers.length === 0 && (
            <p className="cm-empty-hint">{tr("No Cluster Managers yet.")}</p>
          )}

          {!loadingCM && !cmError && clusterManagers.length > 0 && (
            <div className="cm-list">
              {clusterManagers.map((m) => (
                <ClusterManagerRow
                  key={m.id}
                  manager={m}
                  stations={stations}
                  authToken={authToken}
                  onSaved={loadClusterManagers}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;